import { __awaiter, __rest } from "tslib";
import { IntegratedDialogError } from "@tedivo/tedivo-ui";
import { TedivoForm, translateTedivoForm, } from "@tedivo/tedivo-form";
import { SmartTable } from "@tedivo/tedivo-smart-table";
import { getTranslation, i18nReactive } from "../../../app/i18/i18tn";
import BreadcrumbsElement from "../../common/misc/breadcrumbs.element";
import { EditDrawer } from "../../common/EditDrawer";
import { I18nComponentRegisterer } from "@tedivo/tedivo-i18";
import Services from "../../../app/services";
import UserTypeEnum from "../../../app/enums/UserTypeEnum";
import { createTableUserStorageInstance } from "../../../app/preferences/createTableUserStorageInstance";
import { formatDateInCurrentTimezone } from "@tedivo/tedivo-pure-helpers";
import globalStore from "../../../app/stores/globalStore";
import goSquared from "../../../app/tracking/goSquared";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { routeFns } from "../../../app/router/routes";
import router from "../../../app/router";
import { setAppTitle } from "../../../app/setAppTitle";
import { showDeleteModal } from "../../common/ListComponentWithActions";
import topMessageElement from "../../layout/top-tools/getTopMessageElement";
import { z } from "zod";
export class TVDAdminOrgsUsersComponent extends HTMLElement {
    constructor() {
        super();
        this.displayOrganizationUsers = (orgId, orgData) => __awaiter(this, void 0, void 0, function* () {
            globalStore.touchLastUserInteraction();
            const usersWrapper = this.usersWrapper;
            const mainUsersBox = this.mainUsersBox;
            removeChildren(mainUsersBox);
            const actionAddOrgButton = document.createElement("sl-button");
            actionAddOrgButton.innerHTML = getTranslation("general:users.adminCreate");
            actionAddOrgButton.variant = "primary";
            actionAddOrgButton.addEventListener("click", () => {
                this.openOrganizationUserEditModal();
            }, false);
            usersWrapper.withLoading(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                try {
                    const data = orgData ||
                        (yield Services.organizations.getSingleOrganizationAndUsers(orgId))
                            .data;
                    if (!data) {
                        return;
                    }
                    this.data = data;
                    const userOptionsStorageInstance = createTableUserStorageInstance("adminOrgAndUsersList");
                    mainUsersBox.appendChild(createUsersTable({
                        data,
                        actionBox: actionAddOrgButton,
                        userOptionsStorage: userOptionsStorageInstance,
                        onEditUser: (u) => {
                            this.openOrganizationUserEditModal(Object.assign({}, u));
                        },
                        onDeleteUserFromOrg: this.deleteOrganizationUser,
                    }));
                    const editMainIdentityId = (_a = router.currentState) === null || _a === void 0 ? void 0 : _a.userMainIdentity;
                    if (editMainIdentityId &&
                        data.users &&
                        ((_b = data.organization) === null || _b === void 0 ? void 0 : _b.organizationId)) {
                        const users = mergeUsersWithCurrentOrgData((_c = data.organization) === null || _c === void 0 ? void 0 : _c.organizationId, data.users || []);
                        const u = users.find((f) => f.mainIdentityId === editMainIdentityId);
                        if (u) {
                            this.openOrganizationUserEditModal(Object.assign({}, u));
                        }
                    }
                }
                catch (e) {
                    this.showError(e);
                }
            }));
        });
        this.showError = (e) => {
            this.dialogError.show(getTranslation(`errors:errorHasOcurred`), getTranslation(typeof e === "string" ? e : e.translationKey));
        };
        this.openOrganizationUserEditModal = (prevData) => {
            var _a, _b, _c, _d;
            const orgId = router.getRouteParams().id;
            if (!orgId)
                return;
            const editForm = createOrgUserEdit({
                submitButton: this.editDrawer.submitButton,
                orgId,
                prevData,
                showError: this.showError,
                organizationData: (_a = this.data) === null || _a === void 0 ? void 0 : _a.organization,
            });
            const returnUrl = (_b = router.currentState) === null || _b === void 0 ? void 0 : _b.returnUrl;
            if (returnUrl)
                router.clearHistoryViewState();
            if (editForm) {
                const drawer = this.editDrawer.getEditDrawer({
                    title: getTranslation(prevData ? "general:users.adminEdit" : "general:users.adminCreate", {
                        organizationName: (_d = (_c = this.data) === null || _c === void 0 ? void 0 : _c.organization) === null || _d === void 0 ? void 0 : _d.name,
                    }),
                    showUnits: false,
                    readOnlyMode: false,
                    onCancel: () => {
                        if (returnUrl) {
                            router.navigate(returnUrl);
                        }
                    },
                    onSave: () => __awaiter(this, void 0, void 0, function* () {
                        this.editDrawer.disabled = true;
                        const valid = yield editForm.submitFunction();
                        this.editDrawer.disabled = false;
                        if (returnUrl) {
                            router.navigate(returnUrl);
                        }
                        else {
                            this.displayOrganizationUsers(orgId);
                        }
                        return valid;
                    }),
                });
                drawer.appendChild(editForm.node);
                drawer.show();
            }
        };
        this.deleteOrganizationUser = (orgId, dta) => {
            const dialog = this.deleteDialog;
            this.appendChild(dialog);
            const htmlContent = document.createElement("div");
            htmlContent.innerHTML = `${getTranslation("general:users.delete")}<br /><br /><strong>${dta.name} ${dta.familyName}</strong>`;
            showDeleteModal({
                dialog,
                htmlContent,
                deleteLabel: getTranslation("general:common.delete"),
                serviceDeleteCall: () => __awaiter(this, void 0, void 0, function* () {
                    const id = dta.userStatusType !== "EXTERNAL_PROVIDER"
                        ? dta.sub
                        : encodeURI(dta.username);
                    const resp = yield Services.organizations.adminDeleteOrganizationUser(orgId, dta.sub, id);
                    this.displayOrganizationUsers(orgId);
                    return resp;
                }),
            });
        };
        this.i18nCR = new I18nComponentRegisterer(i18nReactive);
        this.dialogError = new IntegratedDialogError(this, false, getTranslation("general:common.close"));
        this.usersWrapper = document.createElement("div-spinner-element");
        this.deleteDialog = document.createElement("sl-dialog");
        this.breadcrumbs = new BreadcrumbsElement();
        this.editDrawer = new EditDrawer();
        this.mainUsersBox = document.createElement("div");
        this.mainUsersBox.className = "oss-card";
        setAppTitle(getTranslation("general:users.users"));
    }
    connectedCallback() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            goSquared.trackPage("TEDIVO Admin Pages - Users");
            goSquared.addEvent("Admin/Organization-Users - Show page");
            const usersWrapper = this.usersWrapper, breadcrumbs = this.breadcrumbs, h1 = document.createElement("h1");
            h1.innerHTML = "<small>loading...</small>";
            breadcrumbs.items = [
                {
                    href: routeFns.adminOrganizations(),
                    label: getTranslation("general:organizations.organizations"),
                },
                { label: "" },
            ];
            usersWrapper.appendChild(this.mainUsersBox);
            this.appendChild(this.editDrawer.drawer);
            this.appendChild(breadcrumbs);
            this.appendChild(h1);
            this.appendChild(usersWrapper);
            const orgId = router.getRouteParams().id;
            if (!orgId) {
                router.navigate(routeFns.adminOrganizations());
                return;
            }
            usersWrapper.setLoading(true);
            const resp = yield Services.organizations.getSingleOrganizationAndUsers(orgId);
            const orgData = resp.data;
            if (!orgData) {
                router.navigate(routeFns.adminOrganizations());
                return;
            }
            h1.innerHTML = ((_a = orgData.organization) === null || _a === void 0 ? void 0 : _a.name) || "";
            this.displayOrganizationUsers(orgId, orgData);
            this.i18nCR.addConsumer(topMessageElement.element, "general:adminArea", "innerHTML");
        });
    }
    disconnectedCallback() {
        this.i18nCR.disconnect();
    }
}
TVDAdminOrgsUsersComponent.observedAttributes = [];
customElements.define("tvd-admin-orgs-users-component", TVDAdminOrgsUsersComponent);
function createUsersTable({ data, userOptionsStorage, actionBox, onEditUser, onDeleteUserFromOrg, }) {
    var _a;
    const organizationId = ((_a = data.organization) === null || _a === void 0 ? void 0 : _a.organizationId) || "";
    const users = mergeUsersWithCurrentOrgData(organizationId, data.users || []);
    const tableUsers = new SmartTable().initialize({
        className: "tvd-table tvd-table-smart-table",
        data: users,
        defaultSortBy: "name",
        title: getTranslation("general:users.users"),
        fields: [
            {
                name: "name",
                label: getTranslation("general:common.name"),
            },
            {
                name: "familyName",
                label: getTranslation("general:users.familyName"),
            },
            {
                name: "email",
                label: getTranslation("general:common.email"),
            },
            {
                name: "organizationUserEnabled",
                label: getTranslation("general:common.enabled"),
                mapper: (u) => getTranslation(`general:common.${u.organizationUserEnabled === "1" ? "yes" : "no"}`),
            },
            {
                name: "userStatusType",
                label: getTranslation("general:common.status"),
            },
            {
                name: "organizationUserRole",
                label: getTranslation("general:common.type"),
                mapper: (u) => getTranslation(`enums:UserTypeEnum.${u.organizationUserRole}`),
            },
            {
                name: "userCreateDate",
                label: getTranslation("general:common.createdAt"),
                valueFunction: (dta) => dta.userCreateDate
                    ? formatDateInCurrentTimezone(dta.userCreateDate)
                    : "",
                className: "centered",
            },
            {
                name: "userLastModifiedDate",
                label: getTranslation("general:common.modifiedAt"),
                valueFunction: (dta) => dta.userLastModifiedDate
                    ? formatDateInCurrentTimezone(dta.userLastModifiedDate)
                    : "",
                className: "centered",
            },
            {
                name: "sub",
                label: getTranslation("general:common.edit"),
                type: "icon",
                icon: "pencil",
                notSortable: true,
                fixedWidth: "60px",
                onClick: (dta) => {
                    onEditUser(dta);
                },
            },
            {
                name: "sub",
                label: getTranslation("general:common.delete"),
                type: "icon",
                icon: "trash",
                iconVariant: "danger",
                notSortable: true,
                fixedWidth: "60px",
                onClick: (dta) => {
                    onDeleteUserFromOrg(organizationId, dta);
                },
            },
        ],
        settings: {
            sortEnabled: true,
            sortVisible: true,
            userOptionsStorage,
            filters: {
                textFieldEnabled: true,
                initialTextFieldFilter: userOptionsStorage.retrieveFilterText(),
                textFieldFilterFields: ["name", "familyName", "email"],
            },
            labelSortBy: getTranslation("general:common.sortBy"),
            labelSearch: getTranslation("general:common.filterBy"),
            createSortIconFn: createSortIcon,
            actionBox,
        },
        pkFunction: (dta) => dta.sub,
    });
    return tableUsers;
}
function createOrgUserEdit({ submitButton, orgId, prevData, showError, organizationData, }) {
    var _a, _b;
    const holder = document.createElement("div");
    const availableUserTypeOptions = [
        { value: "USER", label: getTranslation("enums:UserTypeEnum.USER") },
        { value: "ADMIN", label: getTranslation("enums:UserTypeEnum.ADMIN") },
        {
            value: "SUPERADMIN",
            label: getTranslation("enums:UserTypeEnum.SUPERADMIN"),
        },
    ];
    const formFields = [
        [
            {
                name: "name",
                label: "general:common.name",
                type: "textBox",
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.name) || "",
            },
            {
                name: "familyName",
                label: "general:users.familyName",
                type: "textBox",
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.familyName) || "",
            },
        ],
        [
            {
                name: "email",
                label: "general:common.email",
                type: "textBox",
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.email) || "",
                helpText: getTranslation("general:users.domainsHelpText", {
                    domains: ((_a = organizationData === null || organizationData === void 0 ? void 0 : organizationData.domains) === null || _a === void 0 ? void 0 : _a.join(", ")) || "",
                }),
            },
            {
                name: "organizationUserEnabled",
                label: "general:common.enabled",
                type: "checkbox",
                initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserEnabled) !== undefined
                    ? (prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserEnabled) === "1"
                    : true,
                padStart: true,
            },
        ],
        {
            name: "organizationUserRole",
            label: "general:common.type",
            type: "radioButtonList",
            fieldset: true,
            initialValue: (_b = prevData === null || prevData === void 0 ? void 0 : prevData.organizationUserRole) !== null && _b !== void 0 ? _b : "USER",
            options: availableUserTypeOptions,
        },
    ];
    const tedivoForm = new TedivoForm({
        fields: formFields,
        onSubmit: () => undefined,
        formValidator: createFormValidator(false, (organizationData === null || organizationData === void 0 ? void 0 : organizationData.domains) || []),
        submitButton: submitButton,
        formProps: { autoFocusOnFirstInput: true },
        hiddenData: prevData,
    });
    translateTedivoForm({
        tedivoForm,
        getTranslation: getTranslation,
    });
    holder.appendChild(tedivoForm.form);
    return {
        node: holder,
        tedivoForm,
        submitFunction: submitPassedToEditDrawer,
    };
    function submitPassedToEditDrawer() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const validResult = tedivoForm.doSubmitForm();
            if (validResult.success) {
                const values = validResult.data;
                if (prevData === null || prevData === void 0 ? void 0 : prevData.sub) {
                    const data = Object.assign(Object.assign(Object.assign({}, prevData), values), { organizationId: orgId, organizationUserEnabled: values.organizationUserEnabled ? "1" : "0", organizationUserRole: values.organizationUserRole });
                    const resp = yield Services.organizations.adminUpdateOrganizationUser(orgId, data);
                    if (!((_a = resp.data) === null || _a === void 0 ? void 0 : _a.id)) {
                        showError({
                            errorCode: String(resp.statusCode),
                            message: resp.code || "errorModifyingUser",
                            translationKey: resp.message || "errors:errorModifyingUser",
                        });
                        return false;
                    }
                }
                else {
                    const data = Object.assign(Object.assign({}, values), { organizationId: orgId, organizationUserEnabled: values.organizationUserEnabled ? "1" : "0", organizationUserRole: values.organizationUserRole });
                    const resp = yield Services.organizations.adminCreateOrganizationUser(orgId, data);
                    if (!((_b = resp.data) === null || _b === void 0 ? void 0 : _b.id)) {
                        showError({
                            errorCode: String(resp.statusCode),
                            message: resp.code || "errorCreatingUser",
                            translationKey: resp.message || "errors:errorCreatingUser",
                        });
                        return false;
                    }
                }
                return true;
            }
            else {
                return false;
            }
        });
    }
    function createFormValidator(hasPassword, allowedDomains) {
        const formValidator = z
            .object({
            email: z
                .string()
                .email()
                .transform((v) => v === null || v === void 0 ? void 0 : v.toLowerCase()),
            name: z.string().min(1),
            familyName: z.string().min(1),
            organizationUserEnabled: z.boolean(),
            organizationUserRole: z.nativeEnum(UserTypeEnum),
            password: z.string().min(6).optional(),
        })
            .refine((data) => {
            if (data.email) {
                const domain = (data.email.split("@")[1] || "")
                    .toLowerCase()
                    .trim();
                if (allowedDomains.indexOf(domain) < 0)
                    return false;
                return true;
            }
            else
                return false;
        }, {
            path: ["email"],
        })
            .refine((data) => {
            if (hasPassword) {
                return !!data.password;
            }
            return true;
        }, {
            path: ["password"],
        });
        return formValidator;
    }
}
function mergeUserWithCurrentOrgData(orgId, user) {
    const { organizations } = user, rest = __rest(user, ["organizations"]);
    const curr = organizations.find((o) => o.orgId === orgId);
    return Object.assign(Object.assign({}, rest), { organizationUserRole: (curr === null || curr === void 0 ? void 0 : curr.role) || "USER", organizationUserEnabled: (curr === null || curr === void 0 ? void 0 : curr.uEnabled) || "0" });
}
export function mergeUsersWithCurrentOrgData(orgId, users) {
    return users.map((user) => mergeUserWithCurrentOrgData(orgId, user));
}
function createSortIcon(asc) {
    const icon = document.createElement("sl-icon");
    icon.name = asc ? "sort-down-alt" : "sort-up-alt";
    return icon;
}
